import {useEffect, useRef} from "react";

import {debounce} from "@pg-mono/nodash";

export const DYNAMIC_POSITION_ATTRIBUTE = "data-pos";

export interface IDynamicPositionProps {
    [key: string]: number;
}

export const useListDynamicPositions = <TWrapperElementType extends HTMLElement>() => {
    const listWrapperRef = useRef<TWrapperElementType | null>(null);
    const dynamicPosition = useRef(-1);

    const refreshDynamicPositions = () => {
        let vIndex = 0;
        (Array.from(listWrapperRef.current?.children || []) as HTMLElement[]).forEach((child) => {
            const isVisible = child.offsetWidth > 0;
            const skipElement = child.getAttribute(DYNAMIC_POSITION_ATTRIBUTE) === "-1";

            if (!skipElement && isVisible) {
                const currentVIndex = vIndex++;
                child.setAttribute(DYNAMIC_POSITION_ATTRIBUTE, currentVIndex.toString());
            }
        });
    };

    useEffect(() => {
        const handleResize = debounce(refreshDynamicPositions, 50);

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (listWrapperRef.current) {
            refreshDynamicPositions();

            const observer = new MutationObserver((mutationsList) => {
                for (const mutation of mutationsList) {
                    if (mutation.attributeName === "class") {
                        requestAnimationFrame(() => {
                            refreshDynamicPositions();
                        });
                        break;
                    }
                }

                const listMutation = mutationsList.find((m) => {
                    return m.target.nodeName === "UL";
                });
                const list = listMutation ? listMutation.target : null;

                const listItems = list ? Array.from((list as HTMLUListElement).children) : [];
                const positions = listItems.map((li) => li.getAttribute(DYNAMIC_POSITION_ATTRIBUTE)).filter((pos) => pos && pos !== "-1") as string[];
                const missingPositions = findMissingNumbers(positions);
                if (missingPositions.length > 0) {
                    requestAnimationFrame(() => {
                        refreshDynamicPositions();
                    });
                }
            });

            observer.observe(listWrapperRef.current, {attributes: true, childList: true, subtree: true});
            //
            // const listObserver = new MutationObserver((mutations) => {
            //     const listMutation = mutations.find((m) => {
            //         return m.target.nodeName === "UL";
            //     });
            //     const list = listMutation ? listMutation.target : null;
            //
            //     const listItems = list ? Array.from((list as HTMLUListElement).children) : [];
            //     const positions = listItems.map((li) => li.getAttribute(DYNAMIC_POSITION_ATTRIBUTE)).filter((pos) => pos && pos !== "-1") as string[];
            //     const missingPositions = findMissingNumbers(positions);
            //     if (missingPositions.length > 0) {
            //         requestAnimationFrame(() => {
            //             refreshDynamicPositions();
            //         });
            //     }
            // });
            //
            // listObserver.observe(listWrapperRef.current, {childList: true, attributes: true});

            return () => {
                observer.disconnect();
                // listObserver.disconnect();
            };
        }
    }, [listWrapperRef.current]);

    const getDynamicPosition = () => {
        if (dynamicPosition.current === -1) {
            throw new Error("You have to use initializeDynamicPosition before using getDynamicPosition to reset calculation");
        }

        return dynamicPosition.current++;
    };

    const getDynamicPositionProps = (): IDynamicPositionProps => {
        return {
            [DYNAMIC_POSITION_ATTRIBUTE]: getDynamicPosition()
        };
    };

    const initializeDynamicPosition = () => {
        dynamicPosition.current = 0;
    };

    return {
        wrapperProps: {
            ref: listWrapperRef
        },
        initializeDynamicPosition,
        getDynamicPosition,
        getDynamicPositionProps
    };
};

export const disableDynamicPositionProps = () => {
    return {
        [DYNAMIC_POSITION_ATTRIBUTE]: -1
    };
};

export const getDynamicPositionCssSelector = (position: number) => `[${DYNAMIC_POSITION_ATTRIBUTE}="${position}"]`;

export const getDynamicPositionsCssSelector = (position: number[]) => position.map(getDynamicPositionCssSelector).join(", ");

function findMissingNumbers(arr: string[]): number[] {
    const numArr = arr.map(Number);

    numArr.sort((a, b) => a - b);
    const missingNumbers: number[] = [];

    for (let i = 0; i < numArr[numArr.length - 1]; i++) {
        if (numArr.indexOf(i) === -1) {
            missingNumbers.push(i);
        }
    }

    return missingNumbers;
}
